export interface HeartbeatPingConfig<RESP = unknown, REQ = unknown> {
  intervalMs: number;
  timeoutMs: number;
  makePing: () => REQ | string;
  /**
   * Optional STRICT pong matcher. When provided, only a message it accepts clears a pending
   * pong deadline — other inbound traffic still refreshes the inactivity clock but does not
   * count as a pong. When omitted, any inbound activity satisfies a pending pong.
   * Must not throw; a thrown error is treated as not-a-pong.
   */
  isPong?: (msg: RESP) => boolean;
  /**
   * Consecutive missed pong WINDOWS before onDead. Accounting is per-window, never per-ping id:
   * whatever satisfies the OPEN deadline resets the count — including a late pong that was meant
   * for an earlier probe. That is the intended reading, not a leak: a server still emitting pongs
   * at all is a server that is answering, which is exactly what the counter is asking about.
   * Each miss triggers an immediate re-probe on a fresh timeoutMs deadline instead of waiting out
   * intervalMs, so tolerating N misses costs intervalMs + N*timeoutMs rather than N*intervalMs.
   * Non-finite or sub-1 values fall back to 1. Default 1: die on the first miss.
   */
  missedPongThreshold?: number;
}

/**
 * Non-finite (a NaN out of Number()/an env read, Infinity) and sub-1 thresholds collapse to 1.
 * Without this, `missedPongs >= NaN` is never true and the deadline chain re-probes forever: the
 * transport becomes immortal, pinging a corpse instead of ever handing over to reconnection.
 */
const effectivePongThreshold = (configured?: number): number =>
  configured !== undefined && Number.isFinite(configured) ? Math.max(1, Math.floor(configured)) : 1;

export interface HeartbeatConfig<RESP = unknown, REQ = unknown> {
  /** Watchdog: no inbound bytes for this long → onDead. Off when undefined. */
  inactivityMs?: number;
  /** Client-initiated ping. Off when undefined. */
  ping?: HeartbeatPingConfig<RESP, REQ>;
}

/** Standard Pro feed liveness: 35s inactivity watchdog (v1 SOCKET_NO_ACTIVITY_MS parity). */
export const HEARTBEAT_STANDARD: HeartbeatConfig = { inactivityMs: 35_000 };

interface HeartbeatCallbacks<REQ> {
  sendPing: (frame: REQ | string) => void;
  onDead: () => void;
}

export class Heartbeat<RESP = unknown, REQ = unknown> {
  private inactivityInterval?: ReturnType<typeof setInterval>;
  private pingInterval?: ReturnType<typeof setInterval>;
  private pongTimeout?: ReturnType<typeof setTimeout>;
  private awaitingPong = false;
  private missedPongs = 0;
  private lastActivityTime = 0;

  constructor(
    private config: HeartbeatConfig<RESP, REQ>,
    private callbacks: HeartbeatCallbacks<REQ>,
    private now: () => number = Date.now,
  ) {}

  public start = (): void => {
    this.stop();
    const { inactivityMs, ping } = this.config;
    if (inactivityMs !== undefined) {
      this.lastActivityTime = this.now();
      this.inactivityInterval = setInterval(this.verifyActivity, Math.min(10_000, inactivityMs));
    }
    if (ping !== undefined) {
      this.pingInterval = setInterval(this.sendPing, ping.intervalMs);
    }
  };

  public stop = (): void => {
    clearInterval(this.inactivityInterval);
    this.inactivityInterval = undefined;
    clearInterval(this.pingInterval);
    this.pingInterval = undefined;
    clearTimeout(this.pongTimeout);
    this.pongTimeout = undefined;
    this.awaitingPong = false;
    // start() routes through here first, so a restarted heartbeat always begins on a clean count.
    this.missedPongs = 0;
  };

  public recordActivity = (msg?: RESP): void => {
    // Any inbound byte proves the transport is alive, so the inactivity clock always moves.
    this.lastActivityTime = this.now();
    if (!this.awaitingPong) {
      return;
    }
    const isPong = this.config.ping?.isPong;
    // Strict matching when a matcher is configured: only a real pong closes the pong deadline.
    // Without one, any traffic counts (a matched pong is not required).
    if (isPong !== undefined && (msg === undefined || !this.matchesPong(isPong, msg))) {
      return;
    }
    this.awaitingPong = false;
    clearTimeout(this.pongTimeout);
    this.pongTimeout = undefined;
    // A satisfied pong ends the probe sequence: tolerance is for CONSECUTIVE misses only.
    this.missedPongs = 0;
  };

  /**
   * isPong is consumer code called from the WebSocket onmessage path: letting it throw would
   * abort the frame's 'response' dispatch. A throw counts as not-a-pong, so the pong deadline
   * stands and we fail toward declaring the transport dead — the safe direction.
   */
  private matchesPong = (isPong: (msg: RESP) => boolean, msg: RESP): boolean => {
    try {
      return isPong(msg);
    } catch {
      return false;
    }
  };

  private verifyActivity = (): void => {
    const { inactivityMs } = this.config;
    if (inactivityMs === undefined || this.now() - this.lastActivityTime < inactivityMs) {
      return;
    }
    this.stop(); // do not pile more deaths onto a dying transport
    this.callbacks.onDead();
  };

  /** Interval tick: opens a new probe sequence, never interferes with one already running. */
  private sendPing = (): void => {
    const ping = this.config.ping;
    if (ping === undefined || this.awaitingPong) {
      // A probe is outstanding: its own deadline owns what happens next (re-probe or death).
      // Acting here would either double-count that single unanswered ping as a second miss or
      // put a second ping on the wire against one deadline.
      //
      // Reachable only when timeoutMs >= intervalMs (with the usual timeoutMs < intervalMs the
      // deadline always lands first). At timeoutMs == intervalMs the outcome is identical to the
      // pre-tolerance code either way: both timers come due on the same instant, so whether the
      // tick no-ops and the deadline then fires, or the deadline fires and its stop() kills the
      // interval, threshold 1 yields exactly one onDead at that instant. Only timeoutMs >
      // intervalMs diverges, and only by moving the death from 2*intervalMs out to
      // intervalMs + timeoutMs — later, never sooner.
      return;
    }
    this.probe(ping);
  };

  private probe = (ping: HeartbeatPingConfig<RESP, REQ>): void => {
    this.callbacks.sendPing(ping.makePing());
    this.awaitingPong = true;
    clearTimeout(this.pongTimeout);
    this.pongTimeout = setTimeout(this.handlePongTimeout, ping.timeoutMs);
  };

  private handlePongTimeout = (): void => {
    const ping = this.config.ping;
    if (ping === undefined) {
      return;
    }
    this.missedPongs += 1;
    if (this.missedPongs >= effectivePongThreshold(ping.missedPongThreshold)) {
      this.stop(); // do not pile more deaths onto a dying transport
      this.callbacks.onDead();
      return;
    }
    // Fast re-probe: a miss is the reason to ask AGAIN NOW, on a fresh deadline — waiting out
    // the remaining interval would stretch the verdict to N*intervalMs.
    this.probe(ping);
  };
}
